import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  IProponente,
  IProposta,
  IPropostaKanbanTag,
  PropostaMode,
  PropostaTab,
} from "../types/proposta";
import { createEmptyProposta } from "../utils/propostaConstants";
import { getLockImovelEdit } from "../utils/propostaMappers";

interface PropostaFormContextValue {
  proposta: IProposta;
  setProposta: React.Dispatch<React.SetStateAction<IProposta>>;
  mode: PropostaMode;
  setMode: (mode: PropostaMode) => void;
  activeTab: PropostaTab;
  setActiveTab: (tab: PropostaTab) => void;
  isReadOnly: boolean;
  lockImovelEdit: boolean;
  editingProponenteIndex: number | null;
  setEditingProponenteIndex: (index: number | null) => void;
  updateProponentes: (proponentes: IProponente[]) => void;
  addCorretor: (item: {
    id: number;
    nome: string;
    creci: string;
    foto_perfil: string;
  }) => void;
  removeCorretor: (id: number) => void;
  kanbanTag: IPropostaKanbanTag | null;
  isDark: boolean;
  selectPopupClassName: string;
}

const PropostaFormContext = createContext<PropostaFormContextValue | null>(
  null,
);

export function PropostaFormProvider({
  children,
  initialProposta,
  initialMode,
  kanbanTag = null,
  isDark = false,
  selectPopupClassName = "proposta-modal-dropdown--light",
}: {
  children: ReactNode;
  initialProposta: IProposta;
  initialMode: PropostaMode;
  kanbanTag?: IPropostaKanbanTag | null;
  isDark?: boolean;
  selectPopupClassName?: string;
}) {
  const [proposta, setProposta] = useState<IProposta>(initialProposta);
  const [mode, setMode] = useState<PropostaMode>(initialMode);
  const [activeTab, setActiveTab] = useState<PropostaTab>(
    initialMode === "create" ? "proponentes" : "resumo",
  );
  const [editingProponenteIndex, setEditingProponenteIndex] = useState<
    number | null
  >(null);

  // create: seleção e campos liberados (sem trava de catálogo no HUB).
  // edit (Pendentes / já salva): trava seleção e campos; valor editável.
  // view / status !== -1: isReadOnly cobre tudo.
  const lockImovelEdit =
    mode !== "create" || getLockImovelEdit(proposta);

  const isReadOnly = useMemo(() => {
    if (mode === "create") return false;
    if (mode === "edit") return false;
    if (proposta.status === undefined) return mode === "view";
    return proposta.status !== -1;
  }, [mode, proposta.status]);

  const updateProponentes = useCallback((proponentes: IProponente[]) => {
    setProposta((prev) => ({ ...prev, proponentes }));
  }, []);

  const addCorretor = useCallback(
    (item: {
      id: number;
      nome: string;
      creci: string;
      foto_perfil: string;
    }) => {
      setProposta((prev) => {
        const exists = prev.corretores?.some((c) => c.id === item.id);
        if (exists) return prev;
        return {
          ...prev,
          corretores: [...(prev.corretores || []), item],
        };
      });
    },
    [],
  );

  const removeCorretor = useCallback((id: number) => {
    setProposta((prev) => ({
      ...prev,
      corretores: (prev.corretores || []).filter((c) => c.id !== id),
    }));
  }, []);

  const value = useMemo(
    () => ({
      proposta,
      setProposta,
      mode,
      setMode,
      activeTab,
      setActiveTab,
      isReadOnly,
      lockImovelEdit,
      editingProponenteIndex,
      setEditingProponenteIndex,
      updateProponentes,
      addCorretor,
      removeCorretor,
      kanbanTag,
      isDark,
      selectPopupClassName,
    }),
    [
      proposta,
      mode,
      activeTab,
      isReadOnly,
      lockImovelEdit,
      editingProponenteIndex,
      updateProponentes,
      addCorretor,
      removeCorretor,
      kanbanTag,
      isDark,
      selectPopupClassName,
    ],
  );

  return (
    <PropostaFormContext.Provider value={value}>
      {children}
    </PropostaFormContext.Provider>
  );
}

export function usePropostaForm() {
  const ctx = useContext(PropostaFormContext);
  if (!ctx) {
    throw new Error("usePropostaForm must be used within PropostaFormProvider");
  }
  return ctx;
}

export function buildInitialProposta(
  idCorretor: number,
  idEmpresa: number,
): IProposta {
  return createEmptyProposta(idCorretor, idEmpresa);
}
